/**
 * The printed page's frame as pure functions (PRD-625): where a pixel on the drawn image falls on the page in
 * points, and where a mark in points or in the numbered image's pixels falls as fractions of the page box,
 * so every overlay scales with the image. Points count from the BOTTOM of the page (PDF space) and pixels
 * from the TOP, so the y axis flips. The fractions mirror `fractions` in admin/screens.py.
 */

/** A rectangle in PDF points: left and right along x, bottom and top along y (bottom < top). */
export interface PointBox {
  readonly left: number;
  readonly bottom: number;
  readonly right: number;
  readonly top: number;
}

/** A rectangle in the pixels of an image, from its top-left corner. */
export interface PixelBox {
  readonly x0: number;
  readonly x1: number;
  readonly y0: number;
  readonly y1: number;
}

/** A place on the page, in points. */
export interface PagePoint {
  readonly x: number;
  readonly y: number;
}

/** A place on the drawn image, in pixels from its top-left corner. */
export interface PixelPoint {
  readonly px: number;
  readonly py: number;
}

/** The pixel size of an image. */
export interface PageFrame {
  readonly width: number;
  readonly height: number;
}

/** A rectangle as fractions of the page box, measured from its top-left corner: what a style's percentages are made of. */
export interface Fractions {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

function clamp(value: number, low: number, high: number): number {
  return Math.min(Math.max(value, low), high);
}

/** A point held to the page box: a drag past the edge lands on the edge. */
export function clampToBox(point: PagePoint, box: PointBox): PagePoint {
  return { x: clamp(point.x, box.left, box.right), y: clamp(point.y, box.bottom, box.top) };
}

/** The page point under a pixel of an image drawn at `frame`, clamped to the page. */
export function pixelToPoint(pixel: PixelPoint, frame: PageFrame, box: PointBox): PagePoint {
  const x = box.left + (pixel.px / frame.width) * (box.right - box.left);
  const y = box.top - (pixel.py / frame.height) * (box.top - box.bottom);
  return clampToBox({ x, y }, box);
}

/** The pixel of an image drawn at `frame` that a page point falls on: the inverse of `pixelToPoint`. */
export function pointToPixel(point: PagePoint, frame: PageFrame, box: PointBox): PixelPoint {
  return {
    px: ((point.x - box.left) / (box.right - box.left)) * frame.width,
    py: ((box.top - point.y) / (box.top - box.bottom)) * frame.height,
  };
}

/** Whether a box has area: a drag that did not move is no box to file. */
export function hasArea(box: PointBox): boolean {
  return box.right > box.left && box.top > box.bottom;
}

/** The rectangle two corners of a drag span, in points: the corners may come in either order. */
export function boxOfDrag(from: PagePoint, to: PagePoint): PointBox {
  return {
    left: Math.min(from.x, to.x),
    bottom: Math.min(from.y, to.y),
    right: Math.max(from.x, to.x),
    top: Math.max(from.y, to.y),
  };
}

/** A mark's place in points as fractions of the page box. */
export function fractionsOfPoints(mark: PointBox, page: PointBox): Fractions {
  const width = page.right - page.left;
  const height = page.top - page.bottom;
  return {
    left: (mark.left - page.left) / width,
    top: (page.top - mark.top) / height,
    width: (mark.right - mark.left) / width,
    height: (mark.top - mark.bottom) / height,
  };
}

/** A mark in the pixels of the numbered image as fractions of that image (the same page, so the same fractions). */
export function fractionsOfPixels(mark: PixelBox, frame: PageFrame): Fractions {
  return {
    left: mark.x0 / frame.width,
    top: mark.y0 / frame.height,
    width: (mark.x1 - mark.x0) / frame.width,
    height: (mark.y1 - mark.y0) / frame.height,
  };
}

/**
 * The CSS that shows one box of a page enlarged, for a window whose aspect ratio is the box's own. The page is
 * drawn at its own scale inside the window: its width is the page's width over the box's, and it is moved so the
 * box's left and top edges sit at the window's. Percentages of the window, so it scales with its column.
 */
export function cropOf(box: PointBox, extent: PointBox): { width: string; left: string; top: string; aspect: string } {
  const boxWidth = box.right - box.left;
  const boxHeight = box.top - box.bottom;
  const pageWidth = extent.right - extent.left;
  const pageTop = extent.top;
  return {
    width: `${(pageWidth / boxWidth) * 100}%`,
    left: `${-((box.left - extent.left) / boxWidth) * 100}%`,
    top: `${-((pageTop - box.top) / boxHeight) * 100}%`,
    aspect: `${boxWidth} / ${boxHeight}`,
  };
}
